// Тонкая обёртка над IndexedDB: хранилище «ключ–значение» (снимок) и outbox (события).

import type { OutboxRecord } from './types.ts';

const DB_NAME = 'english-trainer';
const DB_VERSION = 1;

export interface Store {
  kvGet<T>(key: string): Promise<T | undefined>;
  kvPut(key: string, value: unknown): Promise<void>;
  kvDelete(key: string): Promise<void>;
  outboxPut(rec: OutboxRecord): Promise<void>;
  outboxAll(): Promise<OutboxRecord[]>;
  outboxDelete(ids: string[]): Promise<void>;
  clear(): Promise<void>;
}

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('transaction aborted'));
  });
}

export async function openStore(factory: IDBFactory = indexedDB, name = DB_NAME): Promise<Store> {
  const open = factory.open(name, DB_VERSION);
  open.onupgradeneeded = () => {
    const db = open.result;
    if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'event.id' });
  };
  const db = await req(open);

  return {
    async kvGet<T>(key: string) {
      return (await req(db.transaction('kv').objectStore('kv').get(key))) as T | undefined;
    },
    async kvPut(key, value) {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').put(value, key);
      await done(tx);
    },
    async kvDelete(key) {
      const tx = db.transaction('kv', 'readwrite');
      tx.objectStore('kv').delete(key);
      await done(tx);
    },
    async outboxPut(rec) {
      const tx = db.transaction('outbox', 'readwrite');
      tx.objectStore('outbox').put(rec);
      await done(tx);
    },
    async outboxAll() {
      const all = (await req(db.transaction('outbox').objectStore('outbox').getAll())) as OutboxRecord[];
      return all.sort((a, b) => a.seq - b.seq);
    },
    async outboxDelete(ids) {
      if (!ids.length) return;
      const tx = db.transaction('outbox', 'readwrite');
      const os = tx.objectStore('outbox');
      for (const id of ids) os.delete(id);
      await done(tx);
    },
    async clear() {
      const tx = db.transaction(['kv', 'outbox'], 'readwrite');
      tx.objectStore('kv').clear();
      tx.objectStore('outbox').clear();
      await done(tx);
    },
  };
}
